import * as back from '~/components/layouts/Navigation/BreadcrumbContainer/functional/backLinks';
import {
  combine,
  when,
  propertiesBase,
  portfolioItem,
  propertyItem,
  unitItem,
  propertyChain,
  staticItem,
} from '~/components/layouts/Navigation/BreadcrumbContainer/functional/generators';
import * as match from '~/components/layouts/Navigation/BreadcrumbContainer/functional/matchers';
import type { RouteRule, BreadcrumbContext, BreadcrumbItem } from '~/components/layouts/Navigation/BreadcrumbContainer/functional/types';

const PRIORITY = {
  SECTION_BASE: 700,
  ADD_PROPERTY: 790,
  PROPERTY_DETAIL: 800,
  PROPERTY_EDIT: 830,
  UNIT_DETAIL: 840,
  SERVICE_BILL_PAGE: 845,
  UNIT_ADD: 850,
  OWNER_EDIT: 810,
  OWNER_ADD: 820,
} as const;

const safePropertyBreadcrumb = (additionalItem?: string) => (context: BreadcrumbContext) => {
  const property = context.currentProperty?.();
  const isLoaded = context.isLoaded;

  if (!property || !isLoaded) {
    const breadcrumbs = [
      { label: context.t('Properties'), link: '/portfolios' },
      { label: context.t('Loading') },
      { label: context.t('Loading') },
    ];
    if (additionalItem) {
      breadcrumbs.push({ label: context.t(additionalItem), link: undefined });
    }
    return breadcrumbs;
  }

  const portfolioId = property.portfolio?.id;
  const propertyId = property.id;
  const portfolioName = property.portfolio?.name || context.t('Loading');
  const propertyName = property.name || context.t('Loading');

  const breadcrumbs: BreadcrumbItem[] = [
    { label: context.t('Properties'), link: '/portfolios' },
    { label: portfolioName, link: `/portfolios/${portfolioId}` },
    { label: propertyName, link: `/portfolios/${portfolioId}/properties/${propertyId}` },
  ];

  if (additionalItem) {
    breadcrumbs.push({ label: context.t(additionalItem) });
  }

  return breadcrumbs;
};

export const propertyRules: RouteRule[] = [
  {
    match: match.regex(/^\/portfolios\/[^/]+\/add$/),
    generate: combine(portfolioItem(), staticItem('Add property')),
    backLink: back.goBack,
    priority: PRIORITY.ADD_PROPERTY,
  },
  {
    match: match.and(match.startsWith('/portfolios'), match.or(match.includes('/add-service-bill'), match.includes('/addservicebill'))),
    generate: () => [],
    priority: PRIORITY.SERVICE_BILL_PAGE,
  },
  {
    match: match.and(match.startsWith('/portfolios'), match.includes('/units/add')),
    generate: safePropertyBreadcrumb('Add unit'),
    backLink: back.goBack,
    priority: PRIORITY.UNIT_ADD,
  },
  {
    match: match.and(match.startsWith('/portfolios'), match.includes('/units/custom-view')),
    generate: combine(propertiesBase, portfolioItem(), propertyItem()),
    backLink: back.goBack,
    priority: PRIORITY.UNIT_DETAIL,
  },
  {
    match: match.and(match.startsWith('/portfolios'), match.includes('/units/'), match.not(match.includes('/add'))),
    generate: combine(portfolioItem(), propertyItem(), unitItem()),
    backLink: back.goBack,
    priority: PRIORITY.UNIT_DETAIL,
  },
  {
    match: match.and(match.startsWith('/portfolios'), match.includes('/properties/'), match.endsWith('/edit')),
    generate: combine(portfolioItem(), propertyItem(), staticItem('Edit')),
    backLink: back.goBack,
    priority: PRIORITY.PROPERTY_EDIT,
  },
  {
    match: match.and(match.startsWith('/portfolios'), match.endsWith('/owners/add')),
    generate: staticItem('Add owner'),
    backLink: back.goBack,
    priority: PRIORITY.OWNER_ADD,
  },
  {
    match: match.and(match.startsWith('/portfolios'), match.regex(/\/owners\/\d{5,}/)),
    generate: staticItem('Edit owner'),
    backLink: back.goBack,
    priority: PRIORITY.OWNER_EDIT,
  },
  {
    match: match.and(match.startsWith('/portfolios'), match.includes('/properties/'), match.not(match.includes('/units/'))),
    generate: when((context) => !context.isMobile, propertyChain, combine(portfolioItem(), propertyItem())),
    backLink: back.goBack,
    priority: PRIORITY.PROPERTY_DETAIL,
  },
  {
    match: match.and(match.startsWith('/portfolios'), match.includes('/add-property')),
    generate: staticItem('Add property'),
    backLink: back.goBack,
    priority: PRIORITY.ADD_PROPERTY,
  },
  {
    match: match.startsWith('/portfolios'),
    generate: propertiesBase,
    backLink: back.homeLink,
    priority: PRIORITY.SECTION_BASE,
  },
];
